import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuotesApi } from "../../../context/ApiContext.tsx";
import { QuoteDetails } from "../../../api/ApiTypes.ts";
import { formatCurrency, formatDate } from "../../../utils/dateUtils.ts";
import { CardGrid } from "../../../components/CardGrid.tsx";
import { Card } from "../../../components/Card.tsx";
import { FieldDisplay } from "../../../components/FieldDisplay.tsx";
import Modal from "../../../components/Modal.tsx";
import { RejectQuoteForm } from "../../../components/RejectQuoteForm.tsx";
import { RespondQuoteForm } from "../../../components/RespondQuoteForm.tsx";
import {
    DollarSign,
    Calendar,
    Building,
    Landmark,
    Briefcase,
    MapPin,
    Hash
} from "lucide-react";
import QuoteHeader from "../../../components/QuoteHeader.tsx";
import QuoteActionButtons from "../../../components/QuoteActionButtons.tsx";
import ClientInfoSection from "../../../components/ClientInfoSection.tsx";
import {QuoteDetailsSkeleton} from "./QuoteDetailsSkeleton.tsx";
import {GoogleMap} from "../../../components/google/GoogleMap.tsx";
import {Uad36Alert, Uad36Page} from "../../../components/Uad36Notice.tsx";
import {getReportTypeDisplayName, isUad36Order} from "../orderdetails/orderDetailsDisplay.ts";

export const QuoteDetailsPage: React.FC = () => {
    const navigate = useNavigate();
    const quoteApi = useQuotesApi();
    const { id } = useParams();

    const [quote, setQuote] = useState<QuoteDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [modals, setModals] = useState({ accept: false, decline: false });

    const handleBack = () => navigate("/appraiser/quotes");

    const refreshQuoteDetails = async () => {
        if (!id) return;
        setLoading(true);
        setError(false);
        try {
            const details = await quoteApi.getQuoteDetails(parseInt(id));
            setQuote(details);
        } catch (error) {
            console.error("Failed to fetch quote details:", error);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!id) {
            navigate("/appraiser/quotes");
        } else {
            refreshQuoteDetails();
        }
    }, [id]);

    if (loading) {
        return <QuoteDetailsSkeleton />;
    }

    if (error || !quote) {
        return (
            <div className="text-center p-6">
                <p className="text-red-600">Failed to load quote details.</p>
                <button
                    onClick={refreshQuoteDetails}
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                    Try Again
                </button>
                <button
                    onClick={handleBack}
                    className="mt-4 ml-2 px-4 py-2 bg-slate-400 text-white rounded hover:bg-slate-500"
                >
                    Go Back
                </button>
            </div>
        );
    }

    const uad36Quote = isUad36Order(quote.quote);

    return (
        <Uad36Page uad36={uad36Quote} label="UAD 3.6 Quote Request">
            <div className="flex items-center justify-between mb-6">
                <QuoteHeader
                    loanId={quote.quote.loanId || "Not provided"}
                    quoteId={quote.quote.id}
                    onBack={handleBack}
                />
                <QuoteActionButtons
                    onAccept={() => setModals({ ...modals, accept: true })}
                    onReject={() => setModals({ ...modals, decline: true })}
                />
            </div>

            {uad36Quote && <Uad36Alert/>}

            <CardGrid>
                <Card title="Property Information" colSpan={3}>
                    <div className="border-b border-slate-200 pb-4 mb-4">
                        <FieldDisplay
                            label="Property Address"
                            value={
                                <>
                                    <p className="text-slate-700">{quote.quote.propertyAddressLine1}</p>
                                    {quote.quote.propertyCounty && (
                                        <p className="text-sm text-slate-500">County: {quote.quote.propertyCounty}</p>
                                    )}
                                </>
                            }
                            icon={<MapPin className="h-5 w-5 text-blue-500" />}
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <FieldDisplay label="Property Type" value={quote.propertyType.description} icon={<Building className="h-5 w-5 text-blue-500" />} />
                        <FieldDisplay label="Intended Use" value={quote.intendedUse.description} icon={<Landmark className="h-5 w-5 text-blue-500" />} />
                        <FieldDisplay label="Report Type" value={getReportTypeDisplayName(quote.reportType, quote.quote.deliveryStandard) || "Not specified"} icon={<Briefcase className="h-5 w-5 text-blue-500" />} />
                        <FieldDisplay label="Loan Number" value={quote.quote.loanId || "Not provided"} icon={<Hash className="h-5 w-5 text-blue-500" />} />
                    </div>
                </Card>

                <Card title="Financial & Timeline">
                    <div className="space-y-4">
                        <FieldDisplay label="Appraiser Fee" value={formatCurrency(quote.quote.appraiserFee)} icon={<DollarSign className="h-5 w-5 text-blue-500" />} />
                        <FieldDisplay label="Expected Due Date" value={formatDate(quote.quote.dueDate)} icon={<Calendar className="h-5 w-5 text-blue-500" />} />
                    </div>
                </Card>

                <Card title="Client Information">
                    <ClientInfoSection client={quote.client} />
                </Card>
                {/* Property Maps Section */}
                <Card title={quote.quote.propertyAddressLine1} colSpan={3}>
                    <GoogleMap lat={quote.quote.latitude} lng={quote.quote.longitude} />
                </Card>
            </CardGrid>

            <Modal title="Reject Quote" isOpen={modals.decline} onClose={() => setModals({ ...modals, decline: false })}>
                <RejectQuoteForm
                    onCancel={() => setModals({ ...modals, decline: false })}
                    onSuccess={() => handleBack()}
                    quoteId={quote.quote.id}
                />
            </Modal>

            <Modal title="Respond to Quote" isOpen={modals.accept} onClose={() => setModals({ ...modals, accept: false })}>
                <RespondQuoteForm
                    onCancel={() => setModals({ ...modals, accept: false })}
                    onSuccess={() => refreshQuoteDetails().finally(() => setModals({ ...modals, accept: false }))}
                    quoteId={quote.quote.id}
                    suggestedFee={quote.quote.appraiserFee}
                    uad36={uad36Quote}
                />
            </Modal>
        </Uad36Page>
    );
};
